import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { Target, Users, Waypoints } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import { canSee, ROLES, useSession } from '@/features/auth';
import { buildTiles, TILE_ICONS, useDashboard } from '@/features/commitments';
import type { TileId } from '@/features/commitments';
import { Omnibox, OmniboxTrigger } from '@/features/search';
import { MarkTile } from '@/shared/ui';

import styles from './AppShell.module.css';
import { useLastAppRoute } from './lastAppRoute';
import { UserMenu } from './UserMenu';

/**
 * Powłoka aplikacji.
 *
 * ⚠ Menu jest ŚWIADOMIE TYMCZASOWE (decyzja 56). User: „w testach wyjdzie, jak często
 * i który element jest używany — w ten czas przemodelujemy menu". Dlatego pozycje są
 * JEDNĄ TABLICĄ w jednym miejscu, a nie rozsypanym znacznikiem: przemodelowanie ma być
 * zmianą listy, nie przepisywaniem powłoki. **2026-08-06 ta obietnica została spłacona
 * po raz pierwszy** — przegrupowanie ośmiu list w cztery rodziny to zmiana tej tablicy
 * i nic więcej.
 */
interface NavItem {
  to: string;
  labelKey: string;
  /**
   * Ikona — **tylko dla pozycji BEZ kafla**.
   *
   * ⭐ Pozycje list roboczych biorą ją z `TILE_ICONS`, wspólnego z pulpitem: kafel i pozycja
   * menu opisują tę samą listę, więc nie mogą wyglądać inaczej. Do 2026-08-06 istniały dwie
   * tablice (tu ikony `lucide`, na pulpicie emoji) i komentarz „te same znaki co w menu",
   * który przestał być prawdą w dniu, w którym menu dostało prawdziwe ikony.
   */
  icon?: LucideIcon;
  /** Kafel pulpitu, z którego bierze się liczba **i ikona**. Brak = pozycja bez liczby. */
  tile?: TileId;
  /**
   * Role, dla których pozycja jest widoczna. **Brak listy = dla każdego zalogowanego.**
   *
   * ⚠ Dołożone 2026-08-05 po sprawdzeniu uprawnień „od dołu": menu pokazywało **każdemu**
   * pozycję „Import", choć trasa jest `AdminOnly`.
   *
   * ⭐ Admina dokłada `canSee`, nie ta tablica — tak samo, jak po stronie serwera robi
   * `WithAdmin`. Pozycje są **ukrywane, nie blokowane**: kontrolę trzyma serwer, tu chodzi
   * o to, żeby nie pokazywać drzwi, które i tak są zamknięte.
   */
  roles?: readonly string[];
}

interface NavGroup {
  labelKey: string;
  items: readonly NavItem[];
}

/**
 * Cztery rodziny, nie osiem pozycji pod rząd.
 *
 * ⭐ Podział idzie po rzeczy **nieoczywistej**: co wyzwala robotę i czyja ona jest.
 * Dlatego grupy mają NAZWY, a nie tylko odstęp — sama przerwa mówi „tu jest podział",
 * ale nie mówi jaki. Nagłówek „Surty" robi przy okazji to, czego brakuje od decyzji 38:
 * nazywa rodzinę pojęć, dla której nie ma dziś gdzie postawić wyjaśnienia (tooltipy
 * wycofane decyzją 27, strona „Słownik" jeszcze nie istnieje).
 *
 * ⛔ „Ustawienia" **nie ma tu celowo** — przeniosły się pod kółko użytkownika, bo
 * przestały być miejscem w aplikacji i stały się osobnym widokiem (`SettingsShell`).
 * ⛔ Pozycji „Pulpit" też nie ma: prowadzi tam **znak marki** nad menu (decyzja 26),
 * a user ocenił, że tak wystarczy.
 */
const NAV_GROUPS = [
  {
    labelKey: 'app.nav.group.myWork',
    items: [
      { to: '/commitments', labelKey: 'app.nav.myCommitments', tile: 'hanging' },
      { to: '/iterations/waiting', labelKey: 'app.nav.waitingForIteration', tile: 'iteration' },
    ],
  },
  {
    labelKey: 'app.nav.group.surts',
    items: [
      { to: '/surts/woken', labelKey: 'app.nav.wokenSurts', tile: 'fired' },
      { to: '/surts/waiting', labelKey: 'app.nav.waitingForSurt', tile: 'dormant' },
      { to: '/surts/dead', labelKey: 'app.nav.deadSurts', tile: 'draugr' },
    ],
  },
  {
    labelKey: 'app.nav.group.inbox',
    items: [
      { to: '/inbox', labelKey: 'app.nav.inbox', tile: 'notices' },
      { to: '/launched', labelKey: 'app.nav.launched', tile: 'launched' },
    ],
  },
  {
    labelKey: 'app.nav.group.organisation',
    items: [
      { to: '/team-workload', labelKey: 'app.nav.teamWorkload', icon: Users, roles: [ROLES.leader] },
      // ⛔ Dec. 109: kampanie to narzędzie architekta, także do ODCZYTU. Zwykły użytkownik
      // widzi wyłącznie skutek na swoim zadaniu — „z kampanii «tytuł»" przy zobowiązaniu.
      { to: '/campaigns', labelKey: 'app.nav.campaigns', icon: Target, roles: [ROLES.architect] },
    ],
  },
] as const satisfies readonly NavGroup[];

export function AppShell() {
  const { t } = useTranslation();
  const { data: user } = useSession();

  /*
   * Ślad dla „Wróć" z ustawień. ⭐ Warunku „o ile to nie ustawienia" nie ma i nie jest
   * potrzebny: ta powłoka renderuje się WYŁĄCZNIE w aplikacji roboczej, bo ustawienia
   * są od 2026-08-06 osobną trasą poza nią. Reguła pilnuje się sama — dołożenie kolejnej
   * sekcji ustawień nie wymaga tu żadnej zmiany (`lastAppRoute`).
   *
   * ⚠ Zapisujemy `pathname + search`, nie sam `pathname`: stan panelu i soczewek siedzi
   * w łańcuchu zapytania (spec 4A), więc powrót bez niego wracałby na „ten sam ekran"
   * z rozwalonym widokiem.
   */
  const location = useLocation();
  const remember = useLastAppRoute((state) => state.remember);

  useEffect(() => {
    remember(`${location.pathname}${location.search}`);
  }, [location.pathname, location.search, remember]);

  /*
   * Liczby przy pozycjach menu (obie wybrane makiety — X1 i R1 — pokazują je od początku).
   *
   * ⚠ Bez dodatkowego zapytania: `useDashboard` dzieli pamięć podręczną z pulpitem, więc
   * dla kogoś, kto wszedł przez stronę główną, to zero przelotów. Liczby idą przez to samo
   * `buildTiles`, co kafle, więc **menu i pulpit nie mogą pokazać różnych liczb** — a to
   * jest dokładnie ta klasa rozjazdu, której nikt nigdy nie zgłasza jako błędu, tylko
   * przestaje ufać obu.
   */
  const dashboard = useDashboard();
  const counts = new Map(
    dashboard.data ? buildTiles(dashboard.data).map((tile) => [tile.id, tile]) : [],
  );

  return (
    <div className={styles['shell']}>
      <aside className={styles['rail']}>
        {/* Znak marki JEST linkiem do pulpitu (decyzja 26) — i po decyzji usera
            z 2026-08-06 jedyną drogą tam z menu. */}
        <Link className={styles['brand']} to="/">
          <MarkTile size={28} />
          <span className={styles['brandName']}>{t('app.brand')}</span>
        </Link>

        <div className={styles['hero']}>
          <NavLink
            className={({ isActive }) =>
              `${styles['heroLink']} ${isActive ? styles['heroLinkActive'] : ''}`
            }
            to="/constellation"
          >
            <Waypoints size={18} aria-hidden="true" />
            {t('app.nav.constellation')}
          </NavLink>
        </div>

        {/* `<nav>` z etykietą — na stronie bywa kilka obszarów nawigacji
            (menu, ścieżka, zakładki), a czytnik ekranu rozróżnia je nazwą. */}
        <nav className={styles['nav']} aria-label={t('app.nav.lists')}>
          {NAV_GROUPS.map((group) => {
            const visible = group.items.filter((item) =>
              canSee(user, 'roles' in item ? item.roles : undefined),
            );

            // Grupa bez ani jednej widocznej pozycji nie zostawia po sobie nagłówka —
            // inaczej zwykły user widziałby napis „Organizacja" nad pustym miejscem.
            if (visible.length === 0) {
              return null;
            }

            return (
              <div key={group.labelKey} className={styles['group']}>
                <span className={styles['groupLabel']}>{t(group.labelKey)}</span>

                {visible.map((item) => {
                  const tile = 'tile' in item ? counts.get(item.tile) : undefined;

                  // Ikona listy przychodzi z kafla, własna tylko tam, gdzie kafla nie ma.
                  const Icon =
                    ('icon' in item ? item.icon : undefined) ??
                    ('tile' in item ? TILE_ICONS[item.tile] : undefined);

                  return (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      className={({ isActive }) =>
                        `${styles['navLink']} ${isActive ? styles['navLinkActive'] : ''}`
                      }
                    >
                      {Icon ? <Icon size={17} aria-hidden="true" /> : null}
                      {t(item.labelKey)}

                      {/* Zero nie dostaje plakietki: „0" przy ośmiu pozycjach to osiem
                          rzeczy do przeczytania, z których żadna nic nie wnosi. */}
                      {tile && tile.count > 0 ? (
                        <span
                          className={`${styles['navCount']} ${tile.urgent ? styles['navCountUrgent'] : ''}`}
                        >
                          {tile.count}
                        </span>
                      ) : null}
                    </NavLink>
                  );
                })}
              </div>
            );
          })}
        </nav>
      </aside>

      <main className={styles['main']}>
        {/* Pasek to dwie rzeczy: pole na pełną szerokość i kółko. ⚠ Wcześniej były
            trzy kolumny, żeby wyzwalacz stał w osi ekranu — przy pełnej szerokości
            osi nie ma, więc siatka ustąpiła zwykłemu rzędowi. */}
        <div className={styles['top']}>
          <OmniboxTrigger />
          <UserMenu />
        </div>

        <div className={styles['content']}>
          <Outlet />
        </div>

        <Omnibox />
      </main>
    </div>
  );
}
